import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Box, F0Card, F0Text } from '@factorialco/f0-react'
import { useAiChat } from 'modules/ai/chat/useAiChat'
import { File as FileIcon, Masonry as MasonryIcon } from '@factorialco/f0-react/icons/app'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useOne, useOneFeatureAvailable } from 'modules/ai/lib/useOne'

const OneFooter = ({
  onClickStartEmptySurvey,
  onClickStartWithTemplate,
}: {
  onClickStartEmptySurvey: () => void
  onClickStartWithTemplate: () => void
}) => {
  const { visualizationMode, setOpen, setVisualizationMode } = useAiChat()
  const isFullscreen = visualizationMode === 'fullscreen'

  const handleClickStartWithTemplate = useCallback(() => {
    setVisualizationMode('sidepanel')
    setOpen(false)
    onClickStartWithTemplate()
  }, [])

  if (!isFullscreen) {
    return null
  }

  return (
    <F0Box display="flex" gap="lg" flexDirection="column" width="full">
      <F0Text variant="label" content={i18n.t('surveys.one_footer.or_start_with')} />
      <F0Box display="grid" columns="2" gap="xl" width="full">
        <F0Card
          title={i18n.t('surveys.one_footer.empty_survey')}
          description={i18n.t('surveys.one_footer.empty_survey_description')}
          avatar={{
            icon: FileIcon,
            type: 'icon',
          }}
          onClick={onClickStartEmptySurvey}
        />
        <F0Card
          title={i18n.t('surveys.one_footer.templates')}
          description={i18n.t('surveys.one_footer.templates_description')}
          avatar={{
            icon: MasonryIcon,
            type: 'icon',
          }}
          onClick={handleClickStartWithTemplate}
        />
      </F0Box>
    </F0Box>
  )
}

export const useSurveyOne = ({ canCreateSurvey }: { canCreateSurvey: boolean }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const oneAvailable = useOneFeatureAvailable()
  const { open: isChatOpen, clear: clearChat } = useAiChat()
  const [fullscreen, setFullscreen] = useState(false)
  const [autoOpenChat, setChatOpen] = useState(false)
  const shouldClearOnNextOpen = useRef(false)

  useEffect(() => {
    if (!isChatOpen) {
      setChatOpen(false)
    }
  }, [isChatOpen])

  useEffect(() => {
    if (isChatOpen && shouldClearOnNextOpen.current) {
      shouldClearOnNextOpen.current = false
      clearChat()
    }
  }, [isChatOpen, clearChat])

  const navigateToNewSurvey = useCallback(() => {
    navigate(navigation.engagement.smart_surveys.new.fullPath())
  }, [navigate, navigation.engagement.smart_surveys.new])

  const navigateToTemplates = useCallback(() => {
    navigate(navigation.engagement.smart_surveys.templates.fullPath())
  }, [navigate, navigation.engagement.smart_surveys.templates])

  const oneOptions = useMemo(
    () => ({
      initialMessage: i18n.t('forms.one.chat_initial_message_new'),
      autoOpenChat,
      fullscreen,
      footer:
        canCreateSurvey && fullscreen ? (
          <OneFooter
            onClickStartEmptySurvey={navigateToNewSurvey}
            onClickStartWithTemplate={navigateToTemplates}
          />
        ) : undefined,
      // Stable primitive key to prevent footer effect from looping when footer JSX gets new
      // refs each render (navigateToNewSurvey recreated due to unstable navigation context).
      footerKey: canCreateSurvey && fullscreen,
    }),
    [autoOpenChat, fullscreen, canCreateSurvey, navigateToNewSurvey, navigateToTemplates]
  )
  useOne<null>(['surveys'], null, oneOptions)

  const handleCreateSurvey = useCallback(() => {
    if (oneAvailable) {
      shouldClearOnNextOpen.current = true
      setFullscreen(true)
      setChatOpen(true)
    } else {
      navigateToNewSurvey()
    }
  }, [oneAvailable, navigateToNewSurvey])

  return { handleCreateSurvey }
}
